import React, { Suspense, useEffect, useMemo, useState } from "react";
import { FaHome } from "react-icons/fa";
import { shouldRenderUniversalModuleHeader } from "../utils/modulePresentation";

export default function Tabs({
  tabs,
  active,
  onSelect,
  onClose,
  onCloseAll,
  extra,
  isDarkTheme = false,
}) {
  const mountedTabs = useMountedTabs(tabs, active);

  if (!tabs.length) return null;
  return (
    <div className="min-w-0 space-y-4 sm:space-y-6">
      <div
        className={`flex flex-nowrap items-center gap-2 overflow-x-auto overflow-y-visible rounded-3xl border p-2 shadow-xl backdrop-blur md:flex-wrap md:overflow-visible ${
          isDarkTheme
            ? "border-slate-700 bg-slate-900/85"
            : "border-slate-200/70 bg-white/80"
        }`}
        role="tablist"
      >
        {tabs.map((t) => {
          const isActive = active === t.id;
          const tabId = `tab-${t.id}`;
          const panelId = `panel-${t.id}`;
          return (
            <div
              key={t.id}
              role="tab"
              tabIndex={0}
              onClick={() => onSelect(t.id)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onSelect(t.id);
                }
              }}
              className={`group flex shrink-0 cursor-pointer items-center gap-2 rounded-2xl px-4 py-2 text-sm font-medium transition focus:outline-none focus-visible:ring-2 ${
                isActive
                  ? isDarkTheme
                    ? "bg-gradient-to-r from-indigo-700 via-blue-700 to-cyan-700 text-white shadow-lg focus-visible:ring-blue-500"
                    : "bg-gradient-to-r from-sky-200 via-blue-200 to-blue-100 text-blue-900 shadow-lg focus-visible:ring-blue-300"
                  : isDarkTheme
                    ? "text-slate-300 hover:bg-slate-800 hover:text-white focus-visible:ring-slate-500"
                    : "text-slate-600 hover:bg-slate-100/80 hover:text-slate-800 focus-visible:ring-blue-300"
              }`}
              aria-selected={isActive}
              aria-controls={panelId}
              id={tabId}
            >
              {t.id === "home" && (
                <FaHome
                  className={
                    isActive
                      ? isDarkTheme
                        ? "text-white"
                        : "text-blue-900"
                      : isDarkTheme
                        ? "text-slate-400"
                        : "text-slate-500"
                  }
                />
              )}
              <span className="max-w-[9rem] truncate text-left sm:max-w-[12rem]">{t.title}</span>
              {t.id !== "home" && (
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onClose(t.id);
                  }}
                  className={`ml-1 flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold transition ${
                    isActive
                      ? isDarkTheme
                        ? "bg-slate-200/20 text-white hover:bg-slate-200/30"
                        : "bg-white/70 text-blue-900 hover:bg-white"
                      : isDarkTheme
                        ? "bg-slate-700 text-slate-200 hover:bg-slate-600"
                        : "bg-slate-200/80 text-slate-600 hover:bg-slate-300"
                  }`}
                  aria-label={`Fechar aba ${t.title}`}
                >
                  ×
                </button>
              )}
            </div>
          );
        })}
        {extra && <div className="ml-auto flex items-center gap-2">{extra}</div>}
      </div>
      <div
        className={`dashboard-tab-content min-w-0 ${active === "young-apprentice-invoice" ? "overflow-visible" : "overflow-hidden"} rounded-3xl border p-3 shadow-xl backdrop-blur sm:p-6 ${
          isDarkTheme
            ? "border-transparent bg-slate-900/85 text-slate-100"
            : "border-slate-200/80 bg-white/80"
        }`}
      >
        {tabs.map((t) => {
          const tabId = `tab-${t.id}`;
          const panelId = `panel-${t.id}`;
          const isMounted = mountedTabs.has(t.id);
          if (!isMounted) {
            return null;
          }
          const showUniversalHeader = shouldRenderUniversalModuleHeader(t.id);
          return (
            <div
              key={t.id}
              id={panelId}
              role="tabpanel"
              aria-labelledby={tabId}
              className={active === t.id ? "" : "hidden"}
            >
              {showUniversalHeader && (
                <header className="mb-5 rounded-2xl bg-[#071f54] px-5 py-4 text-white shadow-lg sm:px-6">
                  <h1 className="text-[1.45rem] font-extrabold leading-tight text-white">
                    {t.title}
                  </h1>
                </header>
              )}
              <Suspense
                fallback={
                  <div
                    className={`py-10 text-center ${
                      isDarkTheme ? "text-slate-300" : "text-slate-500"
                    }`}
                  >
                    Carregando...
                  </div>
                }
              >
                <t.Component
                  {...(t.props || {})}
                  closeTab={(options) => onClose(t.id, options)}
                  activateTab={onSelect}
                  tabId={t.id}
                />
              </Suspense>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function useMountedTabs(tabs, active) {
  const tabIds = useMemo(() => tabs.map((tab) => tab.id), [tabs]);
  const [mounted, setMounted] = useState(() =>
    active ? new Set([active]) : new Set(),
  );

  useEffect(() => {
    if (!active) return;
    setMounted((prev) => {
      if (prev.has(active)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(active);
      return next;
    });
  }, [active]);

  useEffect(() => {
    setMounted((prev) => {
      const next = new Set();
      tabIds.forEach((id) => {
        if (prev.has(id)) {
          next.add(id);
        }
      });
      return next;
    });
  }, [tabIds]);

  return mounted;
}
